import React from 'react';
import { MainDataContext } from './context';
import { getRidrectParam, useSetInitialQueryParams } from '../routing/hooks';
import Text from '../../../../shared/Text';
import {
  c42Jwt,
  useAuthenticate,
  useDeauthenticate,
  useGetServerEnv,
  useOnSubmitLoginSuccess,
} from '../auth/hooks';
import {
  FORGOT_PASSWORD_INDEX,
  LOGIN_INDEX,
  RESET_PASSWORD_INDEX,
  SUPPORT_LOGIN_INDEX,
} from '../../../constants/routes';
import { useHistory } from 'react-router';

export const useMainData = () => React.useContext(MainDataContext).mainData;

export const useSetMainData = () => React.useContext(MainDataContext).setMainData;

export const useInitializeApplication = () => {
  const setMainData = useSetMainData();
  const setInitialQueryParams = useSetInitialQueryParams();
  const setInitialState = useSetInitialState();
  const toggleWorkingScreen = useToggleWorkingScreen();

  return async ({ pathname, query }: { pathname: string; query: object }) => {
    setInitialQueryParams(query);
    try {
      await setInitialState({ pathname, query });
      Text.initialize({});
      setMainData((current) => ({
        ...current,
        isInitialized: true,
      }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      toggleWorkingScreen(false);
    }
  };
};

export const useToggleWorkingScreen = () => {
  const setMainData = useSetMainData();

  return (show: boolean) => {
    setMainData((current) => ({
      ...current,
      isValidatingToken: show,
    }));
  };
};

export const useSetInitialState = () => {
  const history = useHistory();
  const getServerEnv = useGetServerEnv();
  const authenticate = useAuthenticate();
  const deauthenticate = useDeauthenticate();
  const toggleWorkingScreen = useToggleWorkingScreen();
  const onSubmitLoginSuccess = useOnSubmitLoginSuccess();

  const getUrlSearchParam = (name: string): string => {
    const searchProps = window.location.search
      .substr(1)
      .split('&')
      .reduce((params, item) => {
        const p = item.split('=');
        params[p[0]] = p[1];
        return params;
      }, {});
    return searchProps[name];
  };

  return async ({ pathname, query }: { pathname: string; query: object }) => {
    const { logout, tt, to } = query as any;
    try {
      await getServerEnv();
      if (logout) {
        deauthenticate();
      } else {
        /**
         * Check to see if user is already authenticated and redirect them to the app that requested
         * authentication if the user is authenticated. Continue to load Login app if the user is
         * not authenticated.
         */
        const loginToken = tt || getUrlSearchParam('tt');

        if (loginToken) {
          return authenticate({ loginToken })
            .then(() => onSubmitLoginSuccess(getRidrectParam(to)))
            .catch(() => toggleWorkingScreen(false));
        }

        c42Jwt
          .verifyAuthenticated()
          .then(() => onSubmitLoginSuccess(getRidrectParam(to)))
          .catch(() => c42Jwt.logout());
      }
    } catch (e) {
      // authenticate failed. Go to login unless we are resetting a password, on the forgotten password page, or on the support login page
      if ([RESET_PASSWORD_INDEX, FORGOT_PASSWORD_INDEX, SUPPORT_LOGIN_INDEX].indexOf(pathname) === -1) {
        history.push(LOGIN_INDEX);
      }
    } finally {
      toggleWorkingScreen(false);
    }
  };
};
